import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Paper,
  Box
} from "@mui/material";
import { ServerConfig } from "../../../connectors/Config";
import { AuthHelper } from "../../controllers/AuthHelper";

/* ===============================
   FORMATTAZIONE BACKEND → UI
   =============================== */
const formatFromBackend = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0,00";
  }

  const number = Number(value);

  if (isNaN(number)) {
    return "0,00";
  }

  return number.toFixed(2).replace(".", ",");
};

const Prezziario = () => {
  const [courses, setCourses] = useState([]);
  const [prices, setPrices] = useState({});

  /* ===============================
     FETCH
     =============================== */
  const fetchCourses = async () => {
    try {
      const user = await AuthHelper.getUserFromIdToken();
      const sub_centro = user.Username;

      const res = await fetch(
        `${ServerConfig.AWS.Serverless.API_BASE_URL_2}/prezziario/search/${sub_centro}`
      );

      if (!res.ok) throw new Error("Errore fetch");

      const data = await res.json();
      setCourses(data || []);

      const initialPrices = {};
      data.forEach(course => {
        initialPrices[course.id_corso] = formatFromBackend(course.prezzo);
      });

      setPrices(initialPrices);
    } catch (err) {
      console.error("Errore caricamento prezziario:", err);
      alert("Errore nel caricamento del prezziario");
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  /* ===============================
     CHANGE (solo numeri + virgola)
     =============================== */
  const handlePriceChange = (courseId, value) => {
    // rimuove tutto tranne numeri e virgola
    let sanitized = value.replace(/[^0-9,]/g, "");

    // una sola virgola
    const parts = sanitized.split(",");
    if (parts.length > 2) return;

    // max 2 decimali
    if (parts[1]?.length > 2) return;

    setPrices(prev => ({
      ...prev,
      [courseId]: sanitized
    }));
  };

  /* ===============================
     BLUR (formatta 2 decimali)
     =============================== */
  const handlePriceBlur = (courseId, value) => {
    if (!value || value === ",") {
      setPrices(prev => ({ ...prev, [courseId]: "0,00" }));
      return;
    }

    const number = Number(value.replace(",", "."));
    if (isNaN(number)) return;

    setPrices(prev => ({
      ...prev,
      [courseId]: number.toFixed(2).replace(".", ",")
    }));
  };

  /* ===============================
     SAVE
     =============================== */
  const handleSavePrices = async () => {
    try {
      const payload = Object.entries(prices).map(([id_corso, prezzo]) => ({
        id_corso,
        prezzo: Number(prezzo.replace(",", "."))
      }));

      const user = await AuthHelper.getUserFromIdToken();
      const sub_centro = user.Username;

      const res = await fetch(
        `${ServerConfig.AWS.Serverless.API_BASE_URL_2}/prezziario/update/${sub_centro}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) throw new Error("Errore salvataggio");

      alert("Prezzi salvati con successo");
    } catch (err) {
      console.error("Errore salvataggio:", err);
      alert("Errore durante il salvataggio del prezziario");
    }
  };

  return (
    <Container sx={{ maxWidth: "1400px", mt: 6, mb: 6 }}>
      {/* HEADER */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          background:
            "linear-gradient(90deg, rgba(4,14,99,1) 0%, rgba(40,80,180,1) 100%)",
          color: "#fff"
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Prezziario corsi
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
          Gestione prezzi dei corsi disponibili
        </Typography>
      </Paper>

      {/* TABELLA */}
      <Paper elevation={3} sx={{ borderRadius: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f7fb" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Nome corso</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} width={200}>
                Prezzo (€)
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {courses.map(course => (
              <TableRow key={course.id_corso}>
                <TableCell>
                  <Typography fontWeight={500}>
                    {course["nome corso"]}
                  </Typography>
                </TableCell>

                <TableCell>
                  <TextField
                    type="text"
                    size="small"
                    fullWidth
                    value={prices[course.id_corso] ?? ""}
                    onChange={(e) =>
                      handlePriceChange(course.id_corso, e.target.value)
                    }
                    onBlur={(e) =>
                      handlePriceBlur(course.id_corso, e.target.value)
                    }
                    inputProps={{
                      inputMode: "decimal",
                      pattern: "[0-9,]*"
                    }}
                    InputProps={{
                      sx: {
                        backgroundColor: "#f5f7fb",
                        borderRadius: 2
                      }
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* CTA */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <Button
          size="large"
          sx={{
            backgroundColor: "rgba(4,14,99,1)",
            color: "#fff",
            fontWeight: "bold",
            px: 6,
            py: 1.5,
            borderRadius: 99,
            boxShadow: "0 8px 20px rgba(4,14,99,0.3)",
            "&:hover": {
              backgroundColor: "rgba(4,14,99,0.9)"
            }
          }}
          onClick={handleSavePrices}
        >
          Salva prezzi
        </Button>
      </Box>
    </Container>
  );
};

export default Prezziario;
